import React from "react"
import { Item } from "@/api"
import SeparablePanel from "@/components/SeparablePanel"
import { ItemStats } from "./ItemTreeTable"

interface ItemMasterDetailProps {
  items: Item[]
  itemStats: Record<Item["品目ID"], ItemStats>
  onSelectionChange?: (items: Item[]) => void
}

const formatMaybe = (v: unknown) => (v === undefined || v === null) ? '-' : String(v)

const ItemMasterDetail: React.FC<ItemMasterDetailProps> = ({ items, itemStats }) => {
  // Item モデルにあるフィールドを直接表示する
  const rows = items.map(item => ({
    id: item.品目ID,
    name: item.品目名,
    行: item.行,
    分類: item.分類,
    製造分類: item.製造分類,
    廃番: item.廃番,
    在庫下限: item.在庫下限?.value,
    在庫上限: item.在庫上限?.value,
    最小発注単位: item.最小発注単位?.value,
    工程時間: item.工程時間?.value,
    当月生産計画: item['当月生産計画']?.value,
    次月生産計画: item['次月生産計画']?.value,
    生産上限: item.生産上限?.value,
    生産下限: item.生産下限?.value,
    データ不備: item['データ不備']?.join(', '),
    出荷件数: item.出荷?.length ?? 0,
    生産件数: item.生産?.length ?? 0,
    在庫件数: item.在庫?.length ?? 0,
    総出荷: itemStats[item.品目ID]?.総出荷,
    総生産: itemStats[item.品目ID]?.総生産,
  }))

  const singleItem = rows.length === 1 ? rows[0] : null

  if (singleItem) {
    const fields: { label: string; value: unknown; numeric?: boolean }[][] = [
      [
        { label: '品目ID', value: singleItem.id },
        { label: '品目名', value: singleItem.name },
      ],
      [
        { label: '分類', value: singleItem.分類 },
        { label: '製造分類', value: singleItem.製造分類 },
      ],
      [
        { label: '行', value: singleItem.行 },
        { label: '廃番', value: singleItem.廃番 },
      ],
      [
        { label: '在庫下限', value: singleItem.在庫下限, numeric: true },
        { label: '在庫上限', value: singleItem.在庫上限, numeric: true },
      ],
      [
        { label: '最小発注単位', value: singleItem.最小発注単位, numeric: true },
        { label: '工程時間', value: typeof singleItem.工程時間 === 'number' ? singleItem.工程時間.toFixed(2) : singleItem.工程時間, numeric: true },
      ],
      [
        { label: '当月生産計画', value: singleItem.当月生産計画, numeric: true },
        { label: '次月生産計画', value: singleItem.次月生産計画, numeric: true },
      ],
      [
        { label: '生産下限', value: singleItem.生産下限, numeric: true },
        { label: '生産上限', value: singleItem.生産上限, numeric: true },
      ],
      [
        { label: '総出荷', value: singleItem.総出荷, numeric: true },
        { label: '総生産', value: singleItem.総生産, numeric: true },
      ],
      [
        { label: 'データ不備', value: singleItem.データ不備 },
      ],
    ]

    return (
      <SeparablePanel toggleable header="マスター情報">
        <div className="py-2 text-sm">
          <table className="w-full">
            <tbody>
              {fields.map((row, i) => (
                <tr key={i} className="odd:bg-white even:bg-gray-50">
                  {row.map((cell, j) => (
                    <React.Fragment key={j}>
                      <td className="p-1 pr-2 text-600 font-medium whitespace-nowrap w-2">{cell.label}</td>
                      <td className={`p-1 pr-4 ${cell.numeric ? 'text-right' : ''}`}>{formatMaybe(cell.value)}</td>
                    </React.Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SeparablePanel>
    )
  }

  return (
    <SeparablePanel toggleable header="マスター情報">
      <div className="py-2 overflow-auto">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr>
              <th className="text-left p-2">ID</th>
              <th className="text-left p-2">品目名</th>
              <th className="text-left p-2">行</th>
              <th className="text-left p-2">分類</th>
              <th className="text-left p-2">製造分類</th>
              <th className="text-left p-2">廃番</th>
              <th className="text-right p-2">在庫下限</th>
              <th className="text-right p-2">在庫上限</th>
              <th className="text-right p-2">最小発注単位</th>
              <th className="text-right p-2">工程時間</th>
              <th className="text-right p-2">当月生産計画</th>
              <th className="text-right p-2">次月生産計画</th>
              <th className="text-right p-2">生産上限</th>
              <th className="text-right p-2">生産下限</th>
              <th className="text-left p-2">データ不備</th>
              <th className="text-right p-2">総出荷</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="odd:bg-white even:bg-gray-50">
                <td className="p-2">{r.id}</td>
                <td className="p-2">{r.name}</td>
                <td className="p-2">{formatMaybe(r.行)}</td>
                <td className="p-2">{formatMaybe(r.分類)}</td>
                <td className="p-2">{formatMaybe(r.製造分類)}</td>
                <td className="p-2">{formatMaybe(r.廃番)}</td>
                <td className="p-2 text-right">{formatMaybe(r.在庫下限)}</td>
                <td className="p-2 text-right">{formatMaybe(r.在庫上限)}</td>
                <td className="p-2 text-right">{formatMaybe(r.最小発注単位)}</td>
                <td className="p-2 text-right">{formatMaybe(r.工程時間.toFixed(2))}</td>
                <td className="p-2 text-right">{formatMaybe(r.当月生産計画)}</td>
                <td className="p-2 text-right">{formatMaybe(r.次月生産計画)}</td>
                <td className="p-2 text-right">{formatMaybe(r.生産上限)}</td>
                <td className="p-2 text-right">{formatMaybe(r.生産下限)}</td>
                <td className="p-2">{formatMaybe(r.データ不備)}</td>
                <td className="p-2 text-right">{formatMaybe(r.総出荷)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SeparablePanel>
  )
}

export default ItemMasterDetail
